<template>
<v-container fluid slot="wgu-before-content" class="wgu-slider-container">
  <v-row justify="center">
  <v-card class='wgu-slider-vcard'>

    <v-system-bar
      color="white"
      height="40"
      class="wgu-slider-system-bar"
    >

      <v-tooltip
        top
        v-if="autoPlayActivated"
      >
        <template
          v-slot:activator="{ on, attrs }"
        >
        <v-btn
          @click="handlePlayerButton()"
          icon
          v-bind="attrs"
          v-on="on"
        >
          <v-icon>
          {{autoPlay.currentlyPlaying ? 'pause' : 'play_arrow'}}
          </v-icon>
        </v-btn>
        </template>
        <span>
          {{
            autoPlay.currentlyPlaying ?
            textHoverStopButton :
            textHoverPlayButton
          }}
         </span>
      </v-tooltip>

      <v-spacer></v-spacer>

      <span class="font-weight-light wgu-timestamp" >{{displayedTimeStamp()}}
      </span>

      <v-spacer></v-spacer>
      <span class="wgu-download-button">
          <v-tooltip
          top
          v-if="downloadActivated && download.show"
          >
            <template
            v-slot:activator="{ on, attrs }"
            >
              <v-btn
                icon
                @click="onDownloadClick"
                v-bind="attrs"
                v-on="on"
                >
                  <v-icon>download</v-icon>
              </v-btn>
            </template>
            <span v-html="textHoverDownloadButton"></span>
          </v-tooltip>

          <v-progress-circular
            v-if="download.inProgress"
            indeterminate
            size="20"
            width=3
          >
          </v-progress-circular>
      </span>


      </v-system-bar>

       <v-card-text class="wgu-vcard-slim">

        <v-slider
          :thumb-color="color"
          v-model="sliderValue"
          :min="minRangeValue"
          :max="maxRangeValue"
          color='pink'
          dense
          :track-color="color"
          :track-fill-color="color"
          prepend-icon="remove"
          @click:prepend="decrement"
          append-icon="add"
          @click:append="increment"
          >
        </v-slider>
      </v-card-text>

      <v-snackbar
        v-model="snackbar.show"
        :timeout="snackbar.timeout"
      >
        {{ snackbar.text }}
      </v-snackbar>
  </v-card>
  </v-row>
</v-container>

</template>

<script>
import { Mapable } from '../../../src/mixins/Mapable'
import LayerUtil from '../../../src/util/Layer';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector'
import TileWmsSource from 'ol/source/TileWMS'
import { FilterHelper } from '../../util/OgcFilter';

export default {
  mixins: [Mapable],
  data () {
    return {
      minRangeValue: null,
      maxRangeValue: null,
      sliderValue: null,
      minTimeStamp: null,
      maxTimeStamp: null,
      snackbar: {
        show: false,
        text: 'Download failed',
        timeout: 2000
      },
      download: {
        inProgress: false,
        show: false,
        type: undefined
      },
      autoPlay: {
        currentlyPlaying: false,
        intervalId: undefined
      }
    }
  },

  props: {
    sharedTimeStamp: {type: Date, required: true},
    referenceTimeRange: {type: String, required: true},
    referenceTimeFrequency: {type: String, required: true},
    locale: {type: String, required: false, default: 'en'},
    color: {type: String, required: false, default: 'red darken-3'},
    referenceTimeLayerId: {type: String, required: false},
    downloadActivated: {type: Boolean, required: false, default: false},
    textHoverDownloadButton: {
      type: String,
      required: false,
      default: 'Download layer'
    },
    textHoverPlayButton: {
      type: String,
      required: false,
      default: 'Start animation'
    },
    textHoverStopButton: {
      type: String,
      required: false,
      default: 'Pause animation'
    },
    autoPlayActivated: {type: Boolean, required: false, default: false},
    autoPlayIntervallMilliSeconds: {type: Number, required: false, default: 1000}
  },

  created () {
    this.setMinMaxValue(this.referenceTimeRange);

    // set inital value of slider
    this.sliderValue = this.timeStampToSliderValue(this.sharedTimeStamp);

    // TODO: if sharedTimeStamp is out of range of the slider
    //       there should be a notification or an info message
  },

  watch: {
    sliderValue (newValue) {
      let newTimeStamp = new Date(this.minTimeStamp);
      switch (this.referenceTimeFrequency) {
        case 'hourly':
        case 'PT1H':
          // add the hours to lower limit of the slider
          newTimeStamp.setUTCHours(newTimeStamp.getUTCHours() + newValue);
          break;
        case 'daily':
        case 'PT1D':
          newTimeStamp.setUTCDate(newTimeStamp.getUTCDate() + newValue);
          break;
        case 'yearly':
        case 'P1Y':
        default:
          newTimeStamp.setUTCFullYear(newValue)
          break;
      }

      this.$emit('sliderHasChanged', newTimeStamp);
    },
    sharedTimeStamp (newTimeStamp) {
      this.sliderValue = this.timeStampToSliderValue(newTimeStamp);
    },
    referenceTimeRange (range) {
      this.setMinMaxValue(range);
    }
  },
  methods: {
    onMapBound () {
      // only show download button if it reference layer can be downloaded
      if (this.referenceTimeLayerId) {
        const downloadLayer = LayerUtil.getLayerByLid(this.referenceTimeLayerId, this.map);
        if (!downloadLayer) {
          return;
        }
        if (downloadLayer instanceof VectorLayer &&
           downloadLayer.get('version') === '2.0.0') {
          this.download.show = true;
          this.download.type = 'WFS';
        } else if (downloadLayer instanceof TileLayer &&
                   downloadLayer.getSource() instanceof TileWmsSource &&
                   downloadLayer.get('downloadableWithWCS')) {
          this.download.show = true;
          this.download.type = 'WCS';
        }
      }
    },
    /**
     * Downloads the reference layer at the currently set timestamp.
     */
    onDownloadClick () {
      this.download.show = false;
      this.download.inProgress = true;

      const isoTimeStamp = this.sharedTimeStamp.toISOString();
      const downloadLayer = LayerUtil.getLayerByLid(this.referenceTimeLayerId, this.map);

      const source = downloadLayer.getSource();
      if (!source) {
        this.snackbar.show = true;
        this.download.inProgress = false;
        this.download.show = true;
        return;
      }

      // WFS
      if (this.download.type === 'WFS') {
        const baseUrl = downloadLayer.get('url');
        const version = downloadLayer.get('version');
        const typeName = downloadLayer.get('typeName');
        const crs = 'EPSG:4326';

        // add filter
        const timeFilters = source.get('timeFilterXml');
        const connectedFilters = FilterHelper.connectFiltersWithAnd(timeFilters, version);

        let url = `
        ${baseUrl}?service=WFS
        &version=${version}
        &request=GetFeature
        &typename=${typeName}
        &outputFormat=application/json
        &srsname=${crs}
        &filter=${encodeURI(connectedFilters)}
        `;
        console.log(url);

        const downloadName = typeName + '_' + isoTimeStamp + '.geojson';

        // TODO: check if request contains image and not XML
        //       otherwise the user downloads the error message
        fetch(url)
          .then(response => {
            return response.blob();
          })
          .then((blob) => {
            const fileURL = window.URL.createObjectURL(blob);
            const fileLink = document.createElement('a');

            fileLink.href = fileURL;
            fileLink.setAttribute('download', downloadName);
            document.body.appendChild(fileLink);

            fileLink.click();
            this.download.inProgress = false;
            this.download.show = true;
          }
          )
          .catch(() => {
            this.download.inProgress = false;
            this.snackbar.show = true;
            this.download.show = true;
          });

      // WMS
      } else if (this.download.type === 'WCS') {
        if (!downloadLayer) {
          this.snackbar.show = true;
          this.download.inProgress = false;
          return;
        }
        // get layer name
        const layersProperty = source.getParams()['LAYERS'];
        // if LAYERS is a list of layers, we return
        if (!layersProperty || layersProperty.includes(',')) {
          this.snackbar.show = true;
          this.download.inProgress = false;
          this.download.show = true;
          return;
        }
        const layerName = layersProperty;

        const urls = source.getUrls();
        const baseUrl = urls[0];
        if (!baseUrl) {
          this.snackbar.show = true;
          this.download.inProgress = false;
          this.download.show = true;
          return;
        }

        let url = `
        ${baseUrl}?SERVICE=WCS
        &VERSION=2.0.1
        &REQUEST=GetCoverage
        &COVERAGEID=${layerName}
        &SUBSET=time("${isoTimeStamp}")
        &FORMAT=GeoTIFF
        `;

        let fileNamePrefix = layerName;
        // if filename contains colon
        const split = layerName.split(':');
        if (split.length === 2) {
          // get part after colon
          fileNamePrefix = split[1];
        }

        let downloadName = `${fileNamePrefix}_${isoTimeStamp}.geotiff`;
        // replace colon with underscore
        downloadName = downloadName.replace(':', '_');

        // TODO: check if request contains image and not XML
        //       otherwise the user downloads the error message
        fetch(url)
          .then(response => {
            return response.blob();
          })
          .then((blob) => {
            const fileURL = window.URL.createObjectURL(blob);
            const fileLink = document.createElement('a');

            fileLink.href = fileURL;
            fileLink.setAttribute('download', downloadName);
            document.body.appendChild(fileLink);

            fileLink.click();
            this.download.inProgress = false;
            this.download.show = true;
          }
          )
          .catch(() => {
            this.download.inProgress = false;
            this.snackbar.show = true;
            this.download.show = true;
          });
      } else {
        this.download.inProgress = false;
        this.snackbar.show = true;
        this.download.show = true;
      }
    },
    /**
     * Set the minimal and maximal value of the slider.
     *
     * @param {string} timeRange The iso formatted timerange e.g. "1990-01-01/2020-01-01".
     */
    setMinMaxValue (timeRange) {
      // set time-intervall
      // extract timestamps from ISO-variable
      const split = timeRange.split('/');
      this.minTimeStamp = new Date(split[0]);
      this.maxTimeStamp = new Date(split[1]);

      const diff = this.maxTimeStamp - this.minTimeStamp;
      let timeUnits;
      switch (this.referenceTimeFrequency) {
        case 'hourly':
        case 'PT1H':
          timeUnits = diff / (1000 * 60 * 60);
          this.minRangeValue = 0;
          this.maxRangeValue = timeUnits;
          break;
        case 'daily':
        case 'PT1D':
          timeUnits = diff / (1000 * 60 * 60 * 24);
          this.minRangeValue = 0;
          this.maxRangeValue = timeUnits;
          break;
        case 'yearly':
        case 'P1Y':
        default:
          this.minRangeValue = this.minTimeStamp.getUTCFullYear();
          this.maxRangeValue = this.maxTimeStamp.getUTCFullYear();
          break;
      }
    },

    timeStampToSliderValue (timeStamp) {
      let sliderUnits;
      // calculate the difference between the lower boundary
      // of the slider and the new timestamp
      const difference = timeStamp - this.minTimeStamp;
      switch (this.referenceTimeFrequency) {
        case 'hourly':
        case 'PT1H':
          // TODO: explain floor
          sliderUnits = Math.floor(difference / (1000 * 60 * 60))
          break;
        case 'daily':
        case 'PT1D':
          // TODO: explain ceil
          sliderUnits = Math.ceil(difference / (1000 * 60 * 60 * 24));
          break;
        case 'yearly':
        case 'P1Y':
        default:
          sliderUnits = timeStamp.getUTCFullYear();
          break;
      }
      return sliderUnits;
    },
    decrement () {
      this.sliderValue--
    },
    increment () {
      this.sliderValue++
    },
    displayedTimeStamp () {
      let options = { year: 'numeric' };

      return this.sharedTimeStamp.toLocaleString(this.locale, options);
    },

    /**
     * Handler for the player button.
     */
    handlePlayerButton () {
      if (this.autoPlay.currentlyPlaying) {
        clearInterval(this.autoPlay.intervalId);
        this.autoPlay.currentlyPlaying = false;
      } else {
        this.autoPlay.currentlyPlaying = true;
        this.autoPlay.intervalId = setInterval(() => {
          this.sliderValue++
        },
        this.autoPlayIntervallMilliSeconds
        );
      }
    }
  }
}

</script>

<style scoped>

.wgu-slider-container {
  z-index: 2;
  position: absolute;
  bottom: 65px;
  /* disable click event on the invisible area */
  pointer-events: none;
}

.wgu-slider-system-bar {
  margin-top: 7px;
  margin-bottom: 3px;
}

.wgu-timestamp{
  font-size: 23px;
}

.wgu-slider-vcard{
  width: 600px;
  /* re-enable click events */
  pointer-events: auto;
}
.wgu-vcard-slim{
  padding: 0px 10px 0px 10px;
}

.wgu-text-slim{
  padding: 10px 0px 0px 0px
}
.wgu-download-button {
  width: 30px;
}

@media only screen and (max-width: 600px) {
  .wgu-slider-container{
    width: 100%;
    top: 56px;
    bottom: unset;
    left: unset;
  }
}

</style>



// WEBPACK FOOTER //
// app/components/slider/Slider.vue